import React, { useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import PublicOfferHeroTemplate from "./PublicOfferHeroTemplate";
import OrganizedTourPublicOfferLanding from "./OrganizedTourPublicOfferLanding";
import HotelOfferSaleHero from "../../../Components/HotelOfferSale/HotelOfferSaleHero";
import "./PublicOfferLinkPage.css";
import "./organizedTourPublicOffer.css";
import "./organizedTourPublicOfferFlights.css";
import "./organizedTourItinerary.css";
import "./DevOfferRenderersGalleryPage.css";

const ORCHID_HERO =
  "https://images.unsplash.com/photo-1564501049412-61c2a3083791?auto=format&fit=crop&w=1800&q=80";

function formatIls(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `₪${Math.round(n).toLocaleString("he-IL")}`;
}

class PreviewErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    if (import.meta.env.DEV) {
      console.error("[OFFER_RENDERER_PREVIEW_FAILED]", {
        renderer: this.props.name,
        message: error?.message || "render_failed",
      });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div className="dev-offer-renderers-gallery__error">
          <strong>Preview error:</strong> {this.state.error.message}
        </div>
      );
    }
    return this.props.children;
  }
}

export default function DevOfferRenderersGalleryPage() {
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    console.info("[OFFER_RENDERERS_GALLERY_RENDER]", {
      route:
        typeof window !== "undefined"
          ? window.location.pathname
          : "/dev/offer-renderers-gallery",
    });
  }, []);

  const heroTemplateVm = useMemo(
    () => ({
      header: {
        packageTaglineHe: "הצעת מלון מותאמת אישית",
        headlineHe: "Orchid Eilat",
        datesLine: "21/06/2026 - 25/06/2026",
      },
      heroImageUrl: ORCHID_HERO,
      flights: {
        stayDatesNoteHe: "תאריכי שהייה לפי החיפוש",
        outbound: { labelHe: "טיסת הלוך", calendarDate: "21/06/2026", timeRange: "07:00-08:00" },
        inbound: { labelHe: "טיסת חזור", calendarDate: "25/06/2026", timeRange: "20:00-21:00" },
        showInboundSection: true,
        baggageLine: "תיק יד + מזוודה",
      },
      hotel: {
        name: "Orchid Eilat",
        starsCount: 5,
        starsEmoji: "⭐⭐⭐⭐⭐",
        locationLine: "Eilat, Israel",
        boardLabel: "Half board HB",
        facilities: ["בריכה", "Wi‑Fi", "חוף פרטי"],
      },
      room: {
        name: "Deluxe Room",
        occupancyLabel: "2 adults",
      },
      price: {
        titleHe: "מחיר סופי",
        amountDisplay: "₪8,538",
        subTitleHe: "$2,904",
      },
      footerCtaHe: "אני מעוניין / אני מעוניינת",
    }),
    []
  );

  const organizedTourVm = useMemo(
    () => ({
      tour_title_he: "טיול לדוגמה - Orchid Eilat Demo",
      route_line_he: "תל אביב - אילת - תל אביב",
      dates_line_he: "21/06/2026 - 25/06/2026",
      hero_image_url: ORCHID_HERO,
      itinerary_text_he: "יום 1: הגעה למלון\nיום 2: זמן חופשי\nיום 3: סיור בעיר",
      additional_info_html: "<p>תוכן דמה להצגת הטאב</p>",
      linked_flights: [],
      terms_text_he: "תנאי דמה לתצוגת DEV בלבד.",
      final_price_ils: 8538,
    }),
    []
  );

  const sections = useMemo(
    () => [
      {
        name: "HotelOfferSaleHero",
        path: "src/Components/HotelOfferSale/HotelOfferSaleHero.jsx",
        hotelOnly: true,
        preview: (
          <HotelOfferSaleHero
            hotelName="Orchid Eilat"
            heroImage={ORCHID_HERO}
            rating={5}
            checkIn="21/06/2026"
            checkOut="25/06/2026"
            nights={4}
            destination="Eilat, Israel"
            tags={["Deluxe Room", "Half board HB", "2 adults", "₪8,538"]}
            onPrimaryCta={() => {}}
          />
        ),
      },
      {
        name: "PublicOfferHeroTemplate",
        path: "src/Pages/Client/PublicOffer/PublicOfferHeroTemplate.jsx",
        hotelOnly: false,
        preview: (
          <PublicOfferHeroTemplate
            vm={heroTemplateVm}
            title="Public Offer Hero Template"
            legalNote="DEV mock בלבד"
            onInterest={() => {}}
            interestBusy={false}
            interestDone={false}
            branding={{ agencyName: "HotelsRiver", logoUrl: null }}
          />
        ),
      },
      {
        name: "OrganizedTourPublicOfferLanding",
        path: "src/Pages/Client/PublicOffer/OrganizedTourPublicOfferLanding.jsx",
        hotelOnly: false,
        preview: (
          <OrganizedTourPublicOfferLanding
            vm={organizedTourVm}
            branding={{ agencyName: "HotelsRiver", phone: "050-0000000" }}
            cust={{ customerName: "לקוח לדוגמה", customerPhone: "050-1111111" }}
            expiryLabel="25/05/2026 18:00"
            hasPrice
            formatIls={formatIls}
            onInterest={() => {}}
            interestBusy={false}
            interestDone={false}
            primaryAgentTitle="HotelsRiver"
            showAgencySubline={false}
          />
        ),
      },
    ],
    [heroTemplateVm, organizedTourVm]
  );

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    sections.forEach((section) => {
      console.info("[OFFER_RENDERER_PREVIEW_MOUNT]", { renderer: section.name });
    });
  }, [sections]);

  return (
    <div className="dev-offer-renderers-gallery" dir="rtl">
      <Helmet>
        <title>DEV Offer Renderers Gallery</title>
      </Helmet>
      <header className="dev-offer-renderers-gallery__header">
        <h1>Offer Renderers Gallery (DEV)</h1>
        <p>תצוגה פנימית של קומפוננטות ההצעה הקיימות עם mock data.</p>
      </header>

      {sections.map((section) => (
        <section key={section.name} className="dev-offer-renderers-gallery__section">
          <div className="dev-offer-renderers-gallery__meta">
            <h2>{section.name}</h2>
            <p>{section.path}</p>
            {section.hotelOnly ? (
              <span className="dev-offer-renderers-gallery__badge">Hotel-only candidate</span>
            ) : null}
          </div>
          <PreviewErrorBoundary name={section.name}>
            <div className="dev-offer-renderers-gallery__preview">{section.preview}</div>
          </PreviewErrorBoundary>
        </section>
      ))}
    </div>
  );
}
